<template>
  <div class="container-fluid py-4 px-3 px-md-4">
    <div class="breadcrumb text-md px-0 mb-4">
      <p>
        <span class="breadcrumb-item" @click="$router.push('/dashboard')">Inicio</span>
        &rsaquo; <span class="breadcrumb-item" @click="$router.push('/informes')">Informes</span>
        &rsaquo; Ventas Totales
      </p>
    </div>

    <!-- Cabecera con botones -->
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
      <div>
        <h5 class="mb-0 fw-semibold">Ventas Totales</h5>
        <p v-if="tipsterNombre" class="text-muted mb-0" style="font-size:13px">
          {{ tipsterNombre }} · {{ labelPeriodo }}
        </p>
      </div>
      <div class="d-flex gap-2 flex-wrap" v-if="ventas.length">
        <button class="inf-btn-export" :disabled="exportandoExcel" @click="exportarExcel">
          <i class="fa fa-file-excel me-1"></i>
          {{ exportandoExcel ? 'Exportando…' : 'Exportar Excel' }}
        </button>
        <button class="inf-btn-pdf" :disabled="generandoPdf" @click="generarPdf">
          <i class="fa fa-file-pdf me-1"></i>
          {{ generandoPdf ? 'Generando…' : 'Generar PDF' }}
        </button>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="inf-error mb-3">
      <i class="fa fa-times-circle"></i> {{ error }}
    </div>

    <!-- Skeleton -->
    <div v-if="cargando" class="inf-skeleton">
      <div class="inf-skeleton-card" v-for="n in 3" :key="n">
        <div class="inf-skeleton-line inf-skeleton-title"></div>
        <div class="inf-skeleton-line" v-for="r in 4" :key="r"></div>
      </div>
    </div>

    <!-- Sin datos -->
    <div v-if="!cargando && cargado && !ventas.length" class="text-muted text-center py-5">
      No hay ventas en este período.
    </div>

    <!-- Totales por moneda -->
    <div v-if="!cargando && ventas.length" class="totales-grid mb-4">
      <div v-for="(t, moneda) in totales" :key="moneda" class="total-card">
        <div class="total-moneda">{{ moneda }}</div>
        <div class="total-importe">{{ fmt(t.neto, moneda) }}</div>
        <div class="total-detalle">
          <span>{{ t.ventas }} ventas</span>
          <span v-if="t.devoluciones > 0" class="total-dev">· {{ t.devoluciones }} devoluciones</span>
        </div>
      </div>
    </div>

    <!-- Tabla de ventas -->
    <div v-if="!cargando && ventas.length" class="card ficha-listado">
      <div class="card-body px-0 pt-0 pb-0">
        <div class="table-responsive">
          <table class="table align-middle mb-0 ventas-tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Referencia</th>
                <th>Producto</th>
                <th>Tipo</th>
                <th class="text-end">Precio</th>
                <th class="text-end">Devuelto</th>
                <th class="text-end">Neto</th>
                <th>Moneda</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(v, i) in ventas"
                :key="i"
                :class="{ 'venta-devuelta': v.totalmente_devuelta }"
              >
                <td class="venta-fecha">{{ formatFecha(v.fecha) }}</td>
                <td class="venta-ref">{{ v.referencia }}</td>
                <td>{{ v.producto }}</td>
                <td>
                  <span :class="['venta-tipo', tipoCls(v.tipo_pago)]">{{ tipoLabel(v.tipo_pago) }}</span>
                </td>
                <td class="text-end tabular">{{ fmt(v.precio, v.moneda) }}</td>
                <td class="text-end tabular">
                  <span v-if="v.devuelto > 0" class="dev-amt">{{ fmt(v.devuelto, v.moneda) }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end tabular fw-semibold">
                  <span :class="{ 'dev-amt': v.totalmente_devuelta }">{{ v.totalmente_devuelta ? '—' : fmt(v.neto, v.moneda) }}</span>
                </td>
                <td>
                  <span :class="['badge-moneda', v.moneda === 'USD' ? 'badge-usd' : 'badge-eur']">{{ v.moneda }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/apiClient.js";

export default {
  name: "InformeVentas",
  data() {
    return {
      tipsterId:      null,
      tipsterNombre:  "",
      desde:          "",
      hasta:          "",
      ventas:         [],
      totales:        {},
      cargando:       false,
      cargado:        false,
      error:          null,
      exportandoExcel: false,
      generandoPdf:   false,
    };
  },
  computed: {
    labelPeriodo() {
      if (!this.desde || !this.hasta) return "";
      if (this.desde === this.hasta) return this.desde;
      return `${this.desde} — ${this.hasta}`;
    },
  },
  async mounted() {
    const q = this.$route.query;
    this.tipsterId = q.tipster_id ? parseInt(q.tipster_id) : null;

    // Calcular desde/hasta según periodo
    const hoy = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const periodo = q.periodo || "mes";

    if (periodo === "custom") {
      this.desde = q.desde || fmt(hoy);
      this.hasta = q.hasta || fmt(hoy);
    } else if (periodo === "hoy") {
      this.desde = this.hasta = fmt(hoy);
    } else if (periodo === "mes") {
      this.desde = fmt(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
      this.hasta = fmt(hoy);
    } else if (periodo === "all") {
      this.desde = "2020-01-01";
      this.hasta = fmt(hoy);
    } else {
      const meses = parseInt(periodo);
      const d = new Date(hoy);
      d.setMonth(d.getMonth() - meses);
      this.desde = fmt(d);
      this.hasta = fmt(hoy);
    }

    if (q.desde) this.desde = q.desde;
    if (q.hasta) this.hasta = q.hasta;

    await this.cargar();
  },
  methods: {
    async cargar() {
      if (!this.tipsterId) {
        this.error = "Tipster no especificado. Vuelve al hub de Informes.";
        return;
      }
      this.cargando = true;
      this.error    = null;
      try {
        const { data } = await api.get("/api/v2/informes/ventas", {
          params: { tipster_id: this.tipsterId, desde: this.desde, hasta: this.hasta },
        });
        if (!data.ok) throw new Error(data.message || "Error");
        this.tipsterNombre = data.tipster || "";
        this.ventas        = data.ventas  || [];
        this.totales       = data.totales || {};
        this.cargado       = true;
      } catch (e) {
        this.error = e?.response?.data?.message || e.message || "Error al cargar ventas.";
      } finally {
        this.cargando = false;
      }
    },

    fmt(val, moneda) {
      const n = parseFloat(val) || 0;
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: moneda || "EUR",
        minimumFractionDigits: 2,
      }).format(n);
    },

    formatFecha(f) {
      if (!f) return "—";
      return f.slice(0, 16).replace("T", " ");
    },

    tipoLabel(t) {
      return { directa: "Directa", renovacion: "Renovación", suscripcion: "Suscripción" }[t] || t;
    },

    tipoCls(t) {
      return { directa: "tipo-directa", renovacion: "tipo-renovacion", suscripcion: "tipo-suscripcion" }[t] || "";
    },

    async exportarExcel() {
      this.exportandoExcel = true;
      try {
        const resp = await api.get("/api/v2/informes/ventas/export", {
          params:       { tipster_id: this.tipsterId, desde: this.desde, hasta: this.hasta },
          responseType: "blob",
        });
        const url  = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", `ventas_${this.tipsterNombre}_${this.desde}_${this.hasta}.xlsx`.replace(/\s+/g, "_"));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        alert("Error al exportar Excel");
      } finally {
        this.exportandoExcel = false;
      }
    },

    generarPdf() {
      this.generandoPdf = true;
      try {
        const html = this.buildPdfHtml();
        const win  = window.open("", "_blank");
        if (!win) {
          alert("Permite ventanas emergentes para generar el PDF.");
          return;
        }
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); }, 700);
      } finally {
        this.generandoPdf = false;
      }
    },

    buildPdfHtml() {
      const logoUrl  = window.location.origin + "/img/quanty.png";
      const fechaGen = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });

      const tipos = { directa: "Directa", renovacion: "Renovación", suscripcion: "Suscripción" };

      const filas = this.ventas.map(v => {
        const devCol  = v.devuelto > 0 ? `<span class="dev">${this.fmt(v.devuelto, v.moneda)}</span>` : "—";
        const netoTxt = v.totalmente_devuelta ? "—" : this.fmt(v.neto, v.moneda);
        const rowCls  = v.totalmente_devuelta ? " class=\"fila-dev\"" : "";
        return `<tr${rowCls}>
          <td>${this.formatFecha(v.fecha)}</td>
          <td>${v.referencia}</td>
          <td>${v.producto}</td>
          <td>${tipos[v.tipo_pago] || v.tipo_pago}</td>
          <td class="r">${this.fmt(v.precio, v.moneda)}</td>
          <td class="r">${devCol}</td>
          <td class="r"><strong>${netoTxt}</strong></td>
          <td>${v.moneda}</td>
        </tr>`;
      }).join("");

      const totalRows = Object.entries(this.totales).map(([m, t]) =>
        `<tr class="total-row">
          <td colspan="4"><strong>Total ${m}</strong></td>
          <td class="r"><strong>${this.fmt(t.bruto, m)}</strong></td>
          <td class="r" style="color:#dc2626"><strong>${t.devuelto > 0 ? this.fmt(t.devuelto, m) : '—'}</strong></td>
          <td class="r"><strong>${this.fmt(t.neto, m)}</strong></td>
          <td>${m}</td>
        </tr>`
      ).join("");

      const resumenFilas = Object.entries(this.totales).map(([m, t]) => `
        <tr><td class="res-lbl">Cantidad de ventas (${m})</td>      <td class="res-val">${t.ventas ?? 0}</td></tr>
        <tr><td class="res-lbl">Importe ventas neto (${m})</td>     <td class="res-val">${this.fmt(t.neto, m)}</td></tr>
        <tr><td class="res-lbl">Cantidad devoluciones (${m})</td>   <td class="res-val">${t.devoluciones ?? 0}</td></tr>
        <tr><td class="res-lbl">Monto devoluciones (${m})</td>      <td class="res-val">${this.fmt(t.devuelto, m)}</td></tr>
      `).join('<tr class="res-sep"><td colspan="2"></td></tr>');

      return `<!DOCTYPE html><html lang="es"><head>
        <meta charset="UTF-8">
        <title>Ventas Totales — ${this.tipsterNombre}</title>
        <style>
          *{box-sizing:border-box;margin:0;padding:0}
          body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:24px}
          .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;border-bottom:2px solid #c8a94a;padding-bottom:12px}
          .logo{height:36px}
          .meta{text-align:right;color:#555;font-size:10px}
          h2{font-size:16px;margin-bottom:2px;color:#111}
          .subtitulo{font-size:11px;color:#666;margin-bottom:14px}
          .resumen-titulo{font-size:12px;font-weight:700;margin-bottom:6px;color:#111}
          table{width:100%;border-collapse:collapse;margin-bottom:12px}
          .resumen-tabla{width:auto;min-width:320px;margin-bottom:20px}
          .resumen-tabla td{padding:3px 8px;border:none;font-size:11px}
          .res-lbl{color:#555}
          .res-val{font-weight:600;text-align:right;padding-left:24px}
          .res-sep td{height:6px}
          th{background:#c8a94a;color:#fff;text-align:left;padding:5px 7px;font-size:10px;text-transform:uppercase}
          td{padding:4px 7px;border-bottom:1px solid #eee;vertical-align:top}
          .r{text-align:right}
          tr:nth-child(even) td{background:#fafafa}
          .fila-dev td{color:#aaa}
          .dev{color:#dc2626}
          .total-row td{background:#f5f0e0;font-weight:600;border-top:2px solid #c8a94a}
          @media print{body{padding:10px}@page{margin:15mm}}
        </style>
      </head><body>
        <div class="header">
          <img src="${logoUrl}" class="logo" onerror="this.style.display='none'">
          <div class="meta">
            <div>Generado el ${fechaGen}</div>
            <div>${this.labelPeriodo}</div>
          </div>
        </div>
        <h2>Ventas Totales — ${this.tipsterNombre}</h2>
        <p class="subtitulo">Período: ${this.labelPeriodo}</p>
        <p class="resumen-titulo">Resumen</p>
        <table class="resumen-tabla">
          <tbody>${resumenFilas}</tbody>
        </table>
        <table>
          <thead><tr>
            <th>Fecha</th><th>Referencia</th><th>Producto</th><th>Tipo</th>
            <th class="r">Precio</th><th class="r">Devuelto</th><th class="r">Neto</th><th>Moneda</th>
          </tr></thead>
          <tbody>${filas}${totalRows}</tbody>
        </table>
      </body></html>`;
    },
  },
};
</script>

<style scoped>
.totales-grid {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.total-card {
  background: var(--q-card);
  border: 1.5px solid var(--q-border);
  border-radius: 10px;
  padding: 1rem 1.4rem;
  min-width: 180px;
}
.total-moneda { font-size: 11px; font-weight: 600; color: var(--q-gold); text-transform: uppercase; letter-spacing: .05em; margin-bottom: 4px; }
.total-importe { font-size: 1.5rem; font-weight: 700; color: var(--q-text); font-variant-numeric: tabular-nums; }
.total-detalle { font-size: 11px; color: var(--q-text-muted, #888); margin-top: 4px; }
.total-dev { color: #dc2626; }

.ventas-tabla th {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--q-text-muted, #888);
  background: transparent;
  border-bottom: 1.5px solid var(--q-border);
  padding: 10px 12px;
}
.ventas-tabla td {
  font-size: 13px;
  padding: 9px 12px;
  border-bottom: 1px solid var(--q-border);
  color: var(--q-text);
}
.ventas-tabla tbody tr:last-child td { border-bottom: none; }
.ventas-tabla tbody tr:hover td { background: rgba(200, 169, 74, 0.04); }
.venta-devuelta td { opacity: .5; }
.venta-fecha { font-variant-numeric: tabular-nums; color: var(--q-text-muted, #888); white-space: nowrap; }
.venta-ref { font-family: monospace; font-size: 12px; }
.tabular { font-variant-numeric: tabular-nums; white-space: nowrap; }
.dev-amt { color: #dc2626; }

.venta-tipo { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; }
.tipo-directa    { background: rgba(16,185,129,.12); color: #059669; }
.tipo-renovacion { background: rgba(99,102,241,.12); color: #6366f1; }
.tipo-suscripcion { background: rgba(245,158,11,.12); color: #d97706; }

.badge-moneda { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
.badge-eur { background: rgba(200,169,74,.15); color: var(--q-gold, #c8a94a); }
.badge-usd { background: rgba(16,185,129,.12); color: #059669; }

.inf-btn-export, .inf-btn-pdf {
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid var(--q-border);
  transition: all .15s;
}
.inf-btn-export { background: var(--q-card); color: var(--q-text); }
.inf-btn-export:hover:not(:disabled) { border-color: var(--q-gold); color: var(--q-gold); }
.inf-btn-pdf { background: var(--q-gold); color: #000; border-color: var(--q-gold); }
.inf-btn-pdf:hover:not(:disabled) { opacity: .85; }
.inf-btn-export:disabled, .inf-btn-pdf:disabled { opacity: .5; cursor: not-allowed; }

.inf-error { background: rgba(239,68,68,.1); border: 1px solid rgba(239,68,68,.3); color: #dc2626; border-radius: 8px; padding: 10px 14px; font-size: 13px; }

.inf-skeleton { display: flex; flex-direction: column; gap: 1rem; }
.inf-skeleton-card { background: var(--q-card); border: 1px solid var(--q-border); border-radius: 10px; padding: 1.5rem; }
.inf-skeleton-line { background: var(--q-border); border-radius: 4px; height: 14px; margin-bottom: 10px; animation: pulse 1.4s ease-in-out infinite; }
.inf-skeleton-title { height: 20px; width: 40%; margin-bottom: 16px; }
@keyframes pulse { 0%,100%{opacity:.6} 50%{opacity:1} }

@media (max-width: 600px) {
  .totales-grid { flex-direction: column; }
  .ventas-tabla th:nth-child(3), .ventas-tabla td:nth-child(3) { display: none; }
}
</style>
